import React from 'react';
import { AnalysisResult, Issue, IssueSeverity, RenderDiff } from '../types';
import { HeadingTree } from './HeadingTree';
import { SchemaCoverage } from './SchemaCoverage';
import { RenderDiffPanel } from './RenderDiffPanel';

interface AnalysisReportProps {
  result: AnalysisResult;
  /** CSR HTML cached from the URL scrape, used for manual SSR re-diff */
  csrHtml?: string | null;
  /** Called when the user pastes their own SSR HTML and we recompute the diff client-side */
  onManualSsrDiff?: (newDiff: RenderDiff) => void;
}

const SeverityPill: React.FC<{ severity: IssueSeverity }> = ({ severity }) => {
  const cls =
    severity === IssueSeverity.CRITICAL ? 'pill-critical'
    : severity === IssueSeverity.WARNING ? 'pill-warning'
    : 'pill-info';
  return <span className={`pill ${cls}`}>{severity}</span>;
};

const IssueCard: React.FC<{ issue: Issue }> = ({ issue }) => (
  <article style={{ borderTop: '1px solid var(--rule)', padding: '20px 0' }}>
    <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <SeverityPill severity={issue.severity} />
        <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--signal)', background: 'var(--signal-wash)', padding: '2px 8px', borderRadius: 2, border: 0 }}>
          {issue.element}
        </code>
      </div>
      {issue.lineNumber && issue.lineNumber > 0 && (
        <span className="meta">Line {issue.lineNumber}</span>
      )}
    </header>
    <p style={{ margin: '0 0 12px', color: 'var(--ink-2)' }}>{issue.description}</p>
    <div style={{ borderLeft: '2px solid var(--signal)', background: 'var(--paper-2)', padding: '10px 14px', borderRadius: 2 }}>
      <div className="eyebrow" style={{ marginBottom: 4 }}>Fix</div>
      <p style={{ margin: 0, fontSize: 14.5, color: 'var(--ink)' }}>{issue.suggestion}</p>
    </div>
  </article>
);

export const AnalysisReport: React.FC<AnalysisReportProps> = ({ result, csrHtml, onManualSsrDiff }) => {
  const critical = result.issues.filter(i => i.severity === IssueSeverity.CRITICAL);
  const warning = result.issues.filter(i => i.severity === IssueSeverity.WARNING);
  const info = result.issues.filter(i => i.severity === IssueSeverity.INFO);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <div className="report-top-grid">
        <div className="card">
          <span className="eyebrow">Summary</span>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 400, lineHeight: 1.3, color: 'var(--ink)', margin: '12px 0 0' }}>
            {result.summary}
          </p>
        </div>

        <div className="card">
          <span className="eyebrow">Issue breakdown</span>
          <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
            <CountStat label="Critical" count={critical.length} tone="bad" />
            <CountStat label="Warnings" count={warning.length} tone="warn" />
            <CountStat label="Info" count={info.length} tone="info" />
          </div>
        </div>
      </div>

      <div className="report-mid-grid">
        <div className="card">
          <span className="eyebrow">Heading hierarchy</span>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 22, margin: '8px 0 20px' }}>
            Document outline
          </h3>
          <HeadingTree headings={result.headingHierarchy} />
        </div>
        <div className="card">
          <span className="eyebrow">Structured data</span>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 22, margin: '8px 0 20px' }}>
            Schema.org coverage
          </h3>
          <SchemaCoverage types={result.schemaTypes} />
        </div>
      </div>

      {result.renderDiff && (
        <RenderDiffPanel
          diff={result.renderDiff}
          csrHtml={csrHtml ?? null}
          onManualSsrDiff={onManualSsrDiff}
        />
      )}

      {result.positiveHighlights.length > 0 && (
        <div className="card card-paper-2">
          <span className="eyebrow">Doing well</span>
          <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {result.positiveHighlights.map((h, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', color: 'var(--ink-2)' }}>
                <span style={{ color: 'var(--score-high)', fontFamily: 'var(--font-mono)' }}>✓</span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0 }}>Detailed analysis</h2>
          <span className="meta">{result.issues.length} issue{result.issues.length === 1 ? '' : 's'}</span>
        </div>
        {result.issues.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)' }}>
            No major issues found. Great job.
          </div>
        ) : (
          <div className="card" style={{ paddingTop: 0, paddingBottom: 0 }}>
            {result.issues.map((issue) => (
              <IssueCard key={issue.id} issue={issue} />
            ))}
          </div>
        )}
      </section>

      <style>{`
        .report-top-grid {
          display: grid;
          grid-template-columns: 1.4fr 1fr;
          gap: 16px;
        }
        .report-mid-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 880px) {
          .report-top-grid, .report-mid-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

const CountStat: React.FC<{ label: string; count: number; tone: 'bad' | 'warn' | 'info' }> = ({ label, count, tone }) => {
  const color = tone === 'bad' ? 'var(--score-low)' : tone === 'warn' ? 'var(--score-mid)' : 'var(--signal)';
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 500, color, lineHeight: 1 }}>
        {count}
      </div>
      <div className="meta" style={{ marginTop: 4 }}>{label}</div>
    </div>
  );
};
